import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, apiErrorMessage } from './api'
import { formatSyncResult, type SyncResult } from './syncResult'

export type SyncKind = 'orders' | 'invoices'

interface SyncJob {
  id: number
  kind: SyncKind
  status: 'running' | 'done' | 'failed'
  result: SyncResult | null
  message: string | null
  startedAt: string
  endedAt: string | null
}

/**
 * Sincronizare pornita in fundal pe server (vezi syncJobs.service.ts din API).
 * Butonul primeste raspunsul imediat, iar hook-ul intreaba de stare la cateva secunde.
 * Daca utilizatorul pleaca de pe pagina si revine, urmarirea se reia din ultima lucrare.
 *
 * `successText` scrie titlul notificarii din rezultat; `invalidate` = listele de reincarcat.
 */
export function useSyncJob(
  kind: SyncKind,
  clientId: number | null,
  opts: { successText: (r: SyncResult) => string; invalidate: string[] },
) {
  const qc = useQueryClient()
  const [jobId, setJobId] = useState<number | null>(null)
  const [starting, setStarting] = useState(false)
  // Lucrarile deja anuntate, ca sa nu apara aceeasi notificare la fiecare revenire pe pagina.
  const announced = useRef(new Set<number>())

  // La deschiderea paginii: exista deja o sincronizare in curs pentru clientul ales?
  const latest = useQuery({
    queryKey: ['sync-latest', kind, clientId],
    queryFn: async () =>
      (await api.get<{ job: SyncJob | null }>('/emag/sync/jobs/latest', { params: { clientId, kind } })).data.job,
    enabled: clientId != null,
  })
  useEffect(() => {
    setJobId(null)
  }, [clientId])
  useEffect(() => {
    const j = latest.data
    if (j && j.status === 'running') setJobId(j.id)
    else if (j) announced.current.add(j.id) // terminata inainte: nu o mai anuntam
  }, [latest.data])

  const job = useQuery({
    queryKey: ['sync-job', jobId],
    queryFn: async () => (await api.get<{ job: SyncJob }>(`/emag/sync/jobs/${jobId}`)).data.job,
    enabled: jobId != null,
    refetchInterval: (q) => (q.state.data?.status === 'running' || !q.state.data ? 3000 : false),
  })

  useEffect(() => {
    const j = job.data
    if (!j || j.status === 'running' || announced.current.has(j.id)) return
    announced.current.add(j.id)
    const detail = j.result?.perCountry ? formatSyncResult(j.result) : ''
    if (j.status === 'done' && j.result) {
      toast.success(`${opts.successText(j.result)}\n${detail}`, { duration: 6000 })
    } else {
      toast.error(`${j.message ?? 'Sincronizare eșuată'}${detail ? `\n${detail}` : ''}`, { duration: 12000 })
    }
    for (const key of opts.invalidate) void qc.invalidateQueries({ queryKey: [key, clientId] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data])

  /** `params` = perioada / țara trimise serverului (vezi SyncOrdersBody); lipsă = implicitul serverului. */
  async function start(params: Record<string, string> = {}) {
    if (clientId == null) return
    setStarting(true)
    try {
      const r = await api.post<{ job: SyncJob }>(`/emag/sync/${kind}`, { clientId, ...params })
      setJobId(r.data.job.id)
      toast.info('Sincronizarea a pornit. Poate dura câteva minute — poți lucra mai departe sau închide pagina.', {
        duration: 6000,
      })
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 12000 })
    } finally {
      setStarting(false)
    }
  }

  const running = starting || (jobId != null && (job.data?.status ?? 'running') === 'running')
  return { start, running }
}
